import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AccountTree,
  Add,
  Business,
  HomeWork,
  TrackChanges,
} from '@mui/icons-material';

import { ILegalTransactionRelationship } from '../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationship';
import { IDocumentDirectoryHasDocument } from '../../Interfaces/IDocumentDirectoryHasDocument';
import { IDocumentDirectory } from '../../Interfaces/IDocumentDirectory';
import { ILegalPhaseFullObject } from '../../Interfaces/ILegalPhase';
import { ILegalTransactionFullObject } from '../../Interfaces/ILegalTransaction';
import { LegalTransactionRealEstates } from './LegalTransactionRealEstates';
import { ILegalTransaction_has_LegalTransactionSpecialFunction } from '../../Interfaces/ILegalTransaction_has_LegalTransactionSpecialFunction';
import { ILegalTransactionGwGFullObject } from '../../Interfaces/ILegalTransactionGwG';
import { ILegalRequirementState } from '../../Interfaces/ILegalRequirementState';
import { IProcessManager } from '../../Interfaces/IProcessManager';
import { ProcessManagerInformationTecking } from './ProcessManagerInformationTecking';
import { AddProcessManagerInformationTrackingDialog } from './AddProcessManagerInformationTrackingDialog';
import { LegalTransactionCompany } from './LegalTransactionCompany';
import { ILegalClient } from '../../Interfaces/ILegalClient';
import { LegalTransactionRelationshipTypeEnum } from '../../Interfaces/ILegalTransactionRelationship/LegalTransactionRelationshipTypeEnum';

interface IProps {
  legalTransaction: ILegalTransactionFullObject;
  legalClients: ILegalClient[];
  //
  relationsships: ILegalTransactionRelationship[];
  setRelationships: Function;
  //
  processManagerArray: IProcessManager[];
  setProcessManagerArray: Function;
  // Documente
  documentArray: IDocumentDirectoryHasDocument[];
  documentDirectoryArray: IDocumentDirectory[];
  setDocumentArray: Function;
  setDocumentDirectoryArray: Function;
  // Phase
  legalPhaseFullObjectArray: ILegalPhaseFullObject[];
  setLegalPhaseFullObjectArray: Function;
  legalRequirementStates: ILegalRequirementState[];
  //
  legalTransaction_has_LegalTransactionSpecialFunctionArray: ILegalTransaction_has_LegalTransactionSpecialFunction[];
  setLegalTransaction_has_LegalTransactionSpecialFunctionArray: Function;
  //
  gwgArray: ILegalTransactionGwGFullObject[];
  setGwgArray: Function;
}

type SubjectKey = 'realestate' | 'company';

/**
 * Vorgangsmanager-Tab im Vorgang.
 *
 * Drei Karten:
 *   I.   Vertragsgegenstände (Grundstücke + Unternehmen, Reihenfolge dynamisch)
 *   II.  Informationsaufbereitung (Platzhalter)
 *   III. Informationsverfolgung (Tracking-Aufträge)
 *
 * Reihenfolge in I: a) ist die Kategorie, in der zuerst etwas angelegt
 * wurde — wenn der Notar zuerst eine Firma einträgt, steht „a. Unternehmen"
 * oben; bei Grundstück zuerst entsprechend andersherum. Maßstab ist die
 * kleinste idRelationship (= ältester Eintrag).
 */
export const ProcessManagerTab: React.FC<IProps> = ({
  legalTransaction,
  legalClients,
  relationsships,
  setRelationships,
  //
  processManagerArray,
  setProcessManagerArray,
  //
  documentArray,
  documentDirectoryArray,
  setDocumentArray,
  setDocumentDirectoryArray,
  //
  legalRequirementStates,
  legalPhaseFullObjectArray,
  setLegalPhaseFullObjectArray,
}) => {
  // Karte "Informationsverfolgung" auch dann anzeigen, wenn nur noch ERLEDIGTE
  // Nachverfolgungen (State 3) existieren — sonst verschwindet eine gerade
  // (automatisch) geschlossene Nachverfolgung ganz aus dem Vorgang. Der
  // Empty-State erscheint nur, wenn gar keine Nachverfolgung (aktiv ODER
  // erledigt) vorhanden ist. Die Tabelle/Zeile zeigt erledigte klar erkennbar.
  const trackingTypeIds = [10, 11, 20, 21, 22];
  const hasAnyTracking = processManagerArray.some(pm =>
    trackingTypeIds.includes(pm.idProcessManagerType)
  );

  /* ---------------------------------------------------------------------- */
  /* Plus-Button auf Karte I: Karten-Dialog mit "Grundstueck" / "Firma"     */
  /* ---------------------------------------------------------------------- */
  const [subjectDialogOpen, setSubjectDialogOpen] = useState(false);
  // Tokens, die wir an die Sub-Components weiterreichen — jeder Klick
  // erhoeht den jeweiligen Counter, was im Child ein useEffect triggert.
  // WICHTIG: Die Sub-Components muessen IMMER gemountet sein, sonst kommt
  // der Trigger nie an. Wir verstecken sie ggf. visuell, mounten sie aber
  // jederzeit.
  const [realEstateAddToken, setRealEstateAddToken] = useState(0);
  const [companyAddToken, setCompanyAddToken] = useState(0);

  /* ---------------------------------------------------------------------- */
  /* Reihenfolge Vertragsgegenstände bestimmen                              */
  /* ---------------------------------------------------------------------- */
  // RealEstate-bezogene Relationships: gleicher ID-Satz wie der Filter in
  // LegalTransactionRealEstates ([1, 3, 9, 8, 12]) — sonst ist hasAnySubject
  // false fuer Vorgaenge mit Signaturbeglaubigung / Allgemeine Anfrage, und
  // das Grundstueck wird zwar im Sub-Component gerendert, aber durch
  // display:none im Wrapper versteckt.
  const realEstateTypeIds = new Set<number>([
    LegalTransactionRelationshipTypeEnum.PurchaseContract, // 1
    LegalTransactionRelationshipTypeEnum.Mortgage, // 3
    LegalTransactionRelationshipTypeEnum.SignatureCertification, // 8
    LegalTransactionRelationshipTypeEnum.LandRegisterQuery, // 9
    LegalTransactionRelationshipTypeEnum.Generic, // 12
  ]);

  const realEstateRels = relationsships.filter(r =>
    realEstateTypeIds.has(r.idRelationshipType)
  );
  const companyRels = relationsships.filter(
    r => r.idRelationshipType === LegalTransactionRelationshipTypeEnum.Company
  );

  const minIdOrInf = (arr: ILegalTransactionRelationship[]): number => {
    if (arr.length === 0) return Number.POSITIVE_INFINITY;
    return arr.reduce(
      (m, r) =>
        r.idRelationship > 0 && r.idRelationship < m ? r.idRelationship : m,
      Number.POSITIVE_INFINITY
    );
  };

  const minRe = minIdOrInf(realEstateRels);
  const minCo = minIdOrInf(companyRels);

  // Default-Reihenfolge: Grundstück zuerst (historisch gewachsen). Wenn
  // beide leer sind, ist die Reihenfolge egal — wir blenden den Bereich
  // sowieso über den Empty-State aus.
  let order: [SubjectKey, SubjectKey] = ['realestate', 'company'];
  if (minCo < minRe) order = ['company', 'realestate'];

  const hasAnySubject = realEstateRels.length > 0 || companyRels.length > 0;

  const realEstateBlock = (
    <LegalTransactionRealEstates
      legalTransaction={legalTransaction}
      relationsships={relationsships}
      setRelationships={setRelationships}
      documentArray={documentArray}
      documentDirectoryArray={documentDirectoryArray}
      setDocumentArray={setDocumentArray}
      setDocumentDirectoryArray={setDocumentDirectoryArray}
      legalPhaseFullObjectArray={legalPhaseFullObjectArray}
      setLegalPhaseFullObjectArray={setLegalPhaseFullObjectArray}
      legalReqStates={legalRequirementStates}
      addTriggerToken={realEstateAddToken}
      showInlineAddButton={false}
    />
  );
  const companyBlock = (
    <LegalTransactionCompany
      legalTransaction={legalTransaction}
      relationsships={relationsships}
      setRelationships={setRelationships}
      addTriggerToken={companyAddToken}
      showInlineAddButton={false}
    />
  );

  const renderSubject = (key: SubjectKey, letter: 'a' | 'b') => {
    if (key === 'realestate') {
      return (
        <Box key={key}>
          <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1 }}>
            <HomeWork fontSize='small' color='action' />
            <Typography variant='subtitle2'>{letter}. Grundstücke</Typography>
          </Stack>
          {realEstateBlock}
        </Box>
      );
    }
    return (
      <Box key={key}>
        <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1 }}>
          <Business fontSize='small' color='action' />
          <Typography variant='subtitle2'>{letter}. Unternehmen</Typography>
        </Stack>
        {companyBlock}
      </Box>
    );
  };

  return (
    <Box sx={{ p: 1 }}>
      <Stack direction='row' alignItems='center' spacing={1.5} sx={{ mb: 0.5 }}>
        <AccountTree color='primary' fontSize='large' />
        <Box>
          <Typography variant='h5'>Vorgangsmanager</Typography>
          <Typography variant='body2' color='text.secondary'>
            Übersicht aller automatisierten Routinen und Vertragsgegenstände zu
            diesem Vorgang.
          </Typography>
        </Box>
      </Stack>

      <Divider sx={{ my: 2 }} />

      {/* ---- Karte 1: Vertragsgegenstände ---- */}
      <Card variant='outlined' sx={{ mb: 3 }}>
        <CardContent>
          <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='flex-start'
            sx={{ mb: 1 }}
          >
            <Stack direction='row' spacing={1} alignItems='center'>
              <HomeWork color='primary' />
              <Typography variant='h6'>I. Vertragsgegenstände</Typography>
            </Stack>
            <Tooltip title='Vertragsgegenstand hinzufügen'>
              <IconButton
                color='primary'
                onClick={() => setSubjectDialogOpen(true)}
              >
                <Add />
              </IconButton>
            </Tooltip>
          </Stack>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Grundstücke und Unternehmen, die Gegenstand dieses Vorgangs sind.
            Diese Objekte sind die Grundlage für anschließende
            Informationsverfolgungen (z. B. Grundbuch-Aktualitätsnachweis oder
            Handelsregister-Nachverfolgung).
          </Typography>

          {/* Sub-Components IMMER mounten, damit der Add-Trigger auch im
              Empty-State ankommt. MUI-Dialoge sind Portals und sind auch
              dann sichtbar, wenn der Container display:none ist. */}
          {!hasAnySubject && (
            <Alert severity='info' sx={{ mb: 1 }}>
              Noch kein Vertragsgegenstand hinterlegt. Klicken Sie oben rechts
              auf das <strong>+</strong>-Symbol, um ein Grundstück oder ein
              Unternehmen hinzuzufügen — dadurch werden Folge-Routinen wie
              Grundbuch-Aktualitätsnachweis oder Handelsregister-Überwachung
              möglich.
            </Alert>
          )}
          <Box sx={{ display: hasAnySubject ? 'block' : 'none' }}>
            {renderSubject(order[0], 'a')}
            <Divider sx={{ my: 2 }} />
            {renderSubject(order[1], 'b')}
          </Box>
        </CardContent>
      </Card>

      {/* ---- Karten-Dialog "Was möchten Sie hinzufügen?" ---- */}
      <Dialog
        open={subjectDialogOpen}
        onClose={() => setSubjectDialogOpen(false)}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>
          <Stack direction='row' spacing={1} alignItems='center'>
            <HomeWork color='primary' />
            <Box>
              <Typography variant='h6' component='div'>
                Vertragsgegenstand hinzufügen
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                Wählen Sie aus, was Sie zu diesem Vorgang erfassen möchten.
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Card variant='outlined' sx={{ height: '100%' }}>
                <CardActionArea
                  onClick={() => {
                    setSubjectDialogOpen(false);
                    setRealEstateAddToken(t => t + 1);
                  }}
                  sx={{ height: '100%', alignItems: 'stretch' }}
                >
                  <CardContent>
                    <Stack
                      direction='row'
                      spacing={1.5}
                      alignItems='flex-start'
                    >
                      <Box
                        sx={{
                          color: 'primary.main',
                          display: 'flex',
                          alignItems: 'center',
                          pt: 0.5,
                        }}
                      >
                        <HomeWork />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Stack
                          direction='row'
                          spacing={1}
                          alignItems='center'
                          sx={{ mb: 0.5 }}
                        >
                          <Typography variant='subtitle1'>
                            Grundstück
                          </Typography>
                          <Chip
                            size='small'
                            label='Grundbuch'
                            variant='outlined'
                          />
                        </Stack>
                        <Typography variant='body2' color='text.secondary'>
                          Adresse, Gemarkung, Blatt und Flurstück. Beim Anlegen
                          prüft DiNo automatisch die Datenbank auf bereits
                          bestehende Grundstücke, um doppelte Einträge zu
                          vermeiden.
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card variant='outlined' sx={{ height: '100%' }}>
                <CardActionArea
                  onClick={() => {
                    setSubjectDialogOpen(false);
                    setCompanyAddToken(t => t + 1);
                  }}
                  sx={{ height: '100%', alignItems: 'stretch' }}
                >
                  <CardContent>
                    <Stack
                      direction='row'
                      spacing={1.5}
                      alignItems='flex-start'
                    >
                      <Box
                        sx={{
                          color: 'primary.main',
                          display: 'flex',
                          alignItems: 'center',
                          pt: 0.5,
                        }}
                      >
                        <Business />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Stack
                          direction='row'
                          spacing={1}
                          alignItems='center'
                          sx={{ mb: 0.5 }}
                        >
                          <Typography variant='subtitle1'>
                            Unternehmen / juristische Person
                          </Typography>
                          <Chip
                            size='small'
                            label='Handelsregister'
                            variant='outlined'
                          />
                        </Stack>
                        <Typography variant='body2' color='text.secondary'>
                          Verknüpft ein bereits hinterlegtes Unternehmen aus den
                          Beteiligten des Vorgangs mit den Vertragsgegenständen
                          — Voraussetzung für Handelsregister- und
                          Gesellschafterlisten-Verfolgung.
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubjectDialogOpen(false)}>Abbrechen</Button>
        </DialogActions>
      </Dialog>

      {/* ---- Karte 2: Informationsaufbereitung (Platzhalter) ---- */}
      <Card variant='outlined' sx={{ mb: 3, opacity: 0.6 }}>
        <CardContent>
          <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1 }}>
            <AccountTree color='action' />
            <Typography variant='h6'>II. Informationsaufbereitung</Typography>
          </Stack>
          <Typography variant='body2' color='text.secondary'>
            Aufbereitung von Dokumenten und Entwürfen aus den im Vorgang
            erfassten Daten.{' '}
          </Typography>
        </CardContent>
      </Card>

      {/* ---- Karte 3: Informationsverfolgung ---- */}
      <Card variant='outlined'>
        <CardContent>
          <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='flex-start'
            sx={{ mb: 1 }}
          >
            <Stack direction='row' spacing={1} alignItems='center'>
              <TrackChanges color='primary' />
              <Typography variant='h6'>III. Informationsverfolgung</Typography>
            </Stack>
            <AddProcessManagerInformationTrackingDialog
              processManagerArray={processManagerArray}
              setProcessManagerArray={setProcessManagerArray}
              relationsships={relationsships}
              setRelationships={setRelationships}
              legalTransaction={legalTransaction}
            />
          </Stack>

          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Automatisierte Routinen, die wiederkehrend prüfen, ob sich für
            diesen Vorgang relevante Änderungen ergeben haben — z. B.
            Grundbuch-Aktualitätsnachweis, Handelsregister-Beobachtung,
            Gesellschafterlisten-Updates oder Notaranderkonto-Salden. Bei einer
            Änderung wird automatisch eine Benachrichtigung ausgelöst.
          </Typography>

          {!hasAnyTracking ? (
            <Alert severity='info'>
              Noch keine Verfolgung eingerichtet. Klicken Sie oben rechts auf
              das <strong>+</strong>-Symbol, um eine neue Informationsverfolgung
              anzulegen.
            </Alert>
          ) : (
            <ProcessManagerInformationTecking
              processManagerArray={processManagerArray}
              setProcessManagerArray={setProcessManagerArray}
              relationsships={relationsships}
              setRelationships={setRelationships}
              legalClients={legalClients}
              legalPhases={legalPhaseFullObjectArray}
              legalReqStates={legalRequirementStates}
              roles={legalTransaction.LegalRoleArray ?? []}
            />
          )}
        </CardContent>
      </Card>
    </Box>
  );
};
